/**
 * Assign Ticket Modal Component
 * @description 담당자 할당 모달
 */

import { useState, useEffect } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger,
  SelectValue, Textarea
} from '@shared/ui/primitives';
import { useAuthStore } from '@/stores/authStore';

interface AssignTicketModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (assignee: string, comment?: string) => Promise<void>;
    currentAssignee?: string | { userId?: string; displayName?: string };
}

interface User {
    userId: string;
    displayName: string;
    email: string;
}

export const AssignTicketModal: React.FC<AssignTicketModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currentAssignee,
}) => {
    const user = useAuthStore((state) => state.user);
    const [assignee, setAssignee] = useState<string>(
        typeof currentAssignee === 'object' ? currentAssignee?.userId || '' : (currentAssignee || '')
    );
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [users, setUsers] = useState<User[]>([]);

    useEffect(() => {
        if (isOpen && user?.userType === 'tenant-user') {
            loadUsers();
        }
    }, [isOpen, user]);

    const loadUsers = async () => {
        if (!user || user.userType !== 'tenant-user') return;

        try {
            // TODO: API 연동 - 사용자 목록 조회
            // 임시로 하드코딩된 사용자 목록 사용
            setUsers([
                { userId: 'youngsoo.kim', displayName: '김영수', email: 'youngsoo.kim@gsretail.com' },
                { userId: 'admin', displayName: '관리자', email: 'admin@gsretail.com' },
            ]);
        } catch (error) {
            console.error('Failed to load users:', error);
        }
    };

    const handleConfirm = async () => {
        if (!assignee) {
            return;
        }

        try {
            setIsLoading(true);
            await onConfirm(assignee, comment.trim() || undefined);
            setComment('');
            onClose();
        } catch (error) {
            console.error('Assign ticket failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setAssignee(
                typeof currentAssignee === 'object' ? currentAssignee?.userId || '' : (currentAssignee || '')
            );
            setComment('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>담당자 할당</DialogTitle>
                    <DialogDescription>
                        티켓의 담당자를 할당합니다.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="assignee">담당자</Label>
                        <Select
                            value={assignee}
                            onValueChange={setAssignee}
                        >
                            <SelectTrigger id="assignee">
                                <SelectValue placeholder="담당자를 선택하세요" />
                            </SelectTrigger>
                            <SelectContent>
                                {users.map((u) => (
                                    <SelectItem key={u.userId} value={u.userId}>
                                        {u.displayName} ({u.email})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="comment">코멘트 (선택사항)</Label>
                        <Textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="할당 사유를 입력하세요"
                            rows={4}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || !assignee}
                    >
                        {isLoading ? '처리 중...' : '할당'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

AssignTicketModal.displayName = 'AssignTicketModal';
